import { requisicaoAutenticada } from './http';

/**
 * Cadastro de empresas (`/api/business`).
 *
 * Separado de `operacao.ts` de propósito: lá se lê a carteira de ICMS, aqui se
 * administra o registro da empresa. As permissões também são outras — listar a
 * carteira exige enxergar a operação; alterar cadastro exige gerir a empresa.
 */

export interface EmpresaResumo {
  readonly id: string;
  readonly taxpayerId: string;
  readonly legalName: string;
  readonly name: string | null;
  readonly emailAddress: string;
  readonly phone: string | null;
  readonly addressCity: string | null;
  readonly addressStateCode: string | null;
  readonly isActive: boolean;
}

export interface PaginaDeEmpresas {
  readonly itens: readonly EmpresaResumo[];
  readonly total: number;
  readonly pagina: number;
  readonly porPagina: number;
}

export interface FiltrosDeEmpresa {
  readonly busca?: string;
  readonly pagina?: number;
  readonly porPagina?: number;
}

export async function listarEmpresas(filtros: FiltrosDeEmpresa = {}): Promise<PaginaDeEmpresas> {
  const params = new URLSearchParams();
  // Só entra o que foi informado: `busca=` vazio faria o backend filtrar por
  // string vazia em vez de não filtrar.
  if (filtros.busca !== undefined && filtros.busca.trim() !== '') {
    params.set('busca', filtros.busca.trim());
  }
  params.set('pagina', String(filtros.pagina ?? 1));
  params.set('porPagina', String(filtros.porPagina ?? 25));

  return requisicaoAutenticada<PaginaDeEmpresas>(`/business?${params.toString()}`);
}

/** Os recortes de Empresas por perfil — a lista inteira ou um papel. */
export type PerfilDeEmpresa = 'todas' | 'clientes' | 'assessores' | 'fornecedores';

/** As opções do "Filtro" de Empresas, marcáveis juntas. */
export type PerfilDoFiltro = 'clientes' | 'assessores' | 'fornecedores' | 'sem-perfil';

/** Os serviços da operação em que um papel se mostra. */
export type ServicoDaOperacao = 'RECORRENTE' | 'PONTUAL' | 'PASSIVOS';

export interface EmpresaComPerfis {
  readonly id: string;
  readonly taxpayerId: string;
  readonly legalName: string;
  readonly name: string | null;
  readonly emailAddress: string;
  readonly addressCity: string | null;
  readonly addressStateCode: string | null;
  readonly isActive: boolean;
  /** O perfil cadastrado da empresa. */
  readonly perfis: {
    readonly cliente: boolean;
    readonly assessor: boolean;
    readonly fornecedor: boolean;
  };
  /**
   * Em que serviços a empresa **atuou** em cada papel — deduzido da operação
   * (contratos, SPED, negociações), porque o cadastro não grava papel por
   * serviço.
   */
  readonly servicos: {
    readonly cliente: readonly ServicoDaOperacao[];
    readonly assessor: readonly ServicoDaOperacao[];
    readonly fornecedor: readonly ServicoDaOperacao[];
  };
}

export interface PaginaDeEmpresasComPerfis {
  readonly itens: readonly EmpresaComPerfis[];
  readonly total: number;
  readonly pagina: number;
  readonly porPagina: number;
  /** Quantas empresas cada perfil tem com a busca atual; `todas`, sem filtro de perfil. */
  readonly contagens: Readonly<Record<'todas' | PerfilDoFiltro, number>>;
}

/**
 * Empresas com os perfis marcados no filtro — quem tem qualquer um deles; sem
 * nenhum marcado, todas.
 */
export async function listarEmpresasPorPerfil(filtros: {
  readonly perfis: readonly PerfilDoFiltro[];
  readonly busca: string;
  readonly pagina: number;
  readonly porPagina: number;
}): Promise<PaginaDeEmpresasComPerfis> {
  const params = new URLSearchParams({
    pagina: String(filtros.pagina),
    porPagina: String(filtros.porPagina),
  });
  if (filtros.perfis.length > 0) params.set('perfis', filtros.perfis.join(','));
  if (filtros.busca.trim() !== '') params.set('busca', filtros.busca.trim());

  return requisicaoAutenticada<PaginaDeEmpresasComPerfis>(`/business/perfis?${params.toString()}`);
}

export async function obterEmpresa(businessId: string): Promise<EmpresaResumo> {
  return requisicaoAutenticada<EmpresaResumo>(`/business/${businessId}`);
}

export interface Colaborador {
  readonly accountId: string;
  readonly personId: string;
  readonly fullName: string;
  readonly emailAddress: string;
  /** Só dígitos (DDD + número), ou `null`: o telefone é opcional. */
  readonly phone: string | null;
  /** RN-311 — vínculos trabalhistas unidos por ", ", como o Deca 1 grava. */
  readonly occupation: string | null;
  readonly papel: 'Gestor' | 'Colaborador';
  readonly assinaPelaEmpresa: boolean;
  readonly isActive: boolean;
}

export async function listarColaboradores(businessId: string): Promise<Colaborador[]> {
  return requisicaoAutenticada<Colaborador[]>(`/business/${businessId}/colaboradores`);
}

export interface DocumentoDaEmpresa {
  readonly id: string;
  readonly type: string;
  readonly fileLocation: string;
  readonly fileContentType: string | null;
  readonly fileSizeBytes: number | null;
  readonly observation: string | null;
}

export async function listarDocumentos(businessId: string): Promise<DocumentoDaEmpresa[]> {
  return requisicaoAutenticada<DocumentoDaEmpresa[]>(`/business/${businessId}/documentos`);
}

/** Os regimes tributários no nome que a consulta à Receita grava. */
export const REGIMES_TRIBUTARIOS: readonly string[] = [
  'Simples Nacional',
  'MEI',
  'Lucro Presumido',
  'Lucro Real',
  'Lucro Arbitrado',
  'Imune',
  'Isenta',
];

/**
 * As opções do select de regime. Um regime gravado com outro nome continua
 * na lista: se sumisse, o select mostraria "Não informado" e o próximo
 * salvar o apagaria.
 */
export function opcoesDeRegime(atual: string): { valor: string; rotulo: string }[] {
  const opcoes = [
    { valor: '', rotulo: 'Não informado' },
    ...REGIMES_TRIBUTARIOS.map((r) => ({ valor: r, rotulo: r })),
  ];
  return atual === '' || REGIMES_TRIBUTARIOS.includes(atual)
    ? opcoes
    : [...opcoes, { valor: atual, rotulo: atual }];
}
